import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sparkles, Users, Plus, Search, Menu, X, Settings, LogOut, Upload } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

const BrokerClients = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    current_score_experian: '',
    current_score_equifax: '',
    current_score_transunion: '',
    goal_score: '',
    notes: ''
  });

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      const filtered = clients.filter(client =>
        client.client_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.client_email.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredClients(filtered);
    } else {
      setFilteredClients(clients);
    }
  }, [searchQuery, clients]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/broker/clients`);
      setClients(response.data);
      setFilteredClients(response.data);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleAddClient = async (e) => {
    e.preventDefault();
    try {
      const clientData = {
        ...formData,
        current_score_experian: formData.current_score_experian ? parseInt(formData.current_score_experian) : null,
        current_score_equifax: formData.current_score_equifax ? parseInt(formData.current_score_equifax) : null,
        current_score_transunion: formData.current_score_transunion ? parseInt(formData.current_score_transunion) : null,
        goal_score: formData.goal_score ? parseInt(formData.goal_score) : null
      };
      
      await axios.post(`${API}/broker/clients`, clientData);
      toast.success('Client added successfully!');
      setShowAddModal(false);
      setFormData({
        client_name: '',
        client_email: '',
        client_phone: '',
        current_score_experian: '',
        current_score_equifax: '',
        current_score_transunion: '',
        goal_score: '',
        notes: ''
      });
      fetchClients();
    } catch (error) {
      toast.error('Failed to add client');
    }
  };

  const handleBulkImport = async () => {
    if (!uploadFile) {
      toast.error('Please select a CSV file');
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);

      const response = await axios.post(`${API}/broker/clients/bulk-import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.errors && response.data.errors.length > 0) {
        toast.warning(`Imported ${response.data.imported_count}/${response.data.total_rows} clients. Some errors occurred.`);
        console.error('Import errors:', response.data.errors);
      } else {
        toast.success(`Successfully imported ${response.data.imported_count} clients!`);
      }
      
      setShowBulkImportModal(false);
      setUploadFile(null);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import clients');
    } finally {
      setImporting(false);
    }
  };

  const downloadSampleCSV = () => {
    const csvContent = 'client_name,client_email,client_phone,current_score_experian,current_score_equifax,current_score_transunion,goal_score,notes\n' +
      'John Doe,john@example.com,(555) 123-4567,650,645,655,720,New client from referral\n' +
      'Jane Smith,jane@example.com,(555) 987-6543,600,595,605,700,Needs comprehensive credit repair';
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'client_import_sample.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl font-heading">Loading clients...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-heading font-bold text-gradient">DISPUTEKING.AI</span>
              <span className="block text-xs text-slate-600">Broker Portal</span>
            </div>
            
            <div className="hidden md:flex items-center gap-6">
              <button onClick={() => navigate('/broker')} className="text-slate-600 hover:text-violet-600 font-medium transition-colors">Dashboard</button>
              <button onClick={() => navigate('/broker/clients')} className="text-violet-600 font-medium">Clients</button>
              <button onClick={() => navigate('/education')} className="text-slate-600 hover:text-violet-600 font-medium transition-colors">Resources</button>
              <button onClick={() => navigate('/settings')} className="text-slate-600 hover:text-violet-600 font-medium transition-colors">
                <Settings className="w-5 h-5" />
              </button>
              <Button onClick={() => { logout(); navigate('/'); }} variant="ghost" className="font-heading">
                <LogOut className="w-5 h-5 mr-2" />
                Logout
              </Button>
            </div>

            <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-4">
              <button onClick={() => navigate('/broker')} className="block w-full text-left text-slate-600 font-medium">Dashboard</button>
              <button onClick={() => navigate('/broker/clients')} className="block w-full text-left text-violet-600 font-medium">Clients</button>
              <button onClick={() => navigate('/education')} className="block w-full text-left text-slate-600 font-medium">Resources</button>
              <button onClick={() => navigate('/settings')} className="block w-full text-left text-slate-600 font-medium">Settings</button>
              <Button onClick={() => { logout(); navigate('/'); }} variant="ghost" className="w-full justify-start">Logout</Button>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-heading font-bold mb-2">Client Management</h1>
            <p className="text-slate-600">{clients.length} total clients</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => setShowBulkImportModal(true)}
              variant="outline"
              className="rounded-full font-heading font-semibold"
            >
              <Upload className="w-5 h-5 mr-2" />
              Bulk Import
            </Button>
            <Button
              data-testid="add-new-client-button"
              onClick={() => setShowAddModal(true)}
              className="bg-violet-600 hover:bg-violet-700 text-white rounded-full font-heading font-semibold shadow-lg hover:scale-105 transition-all"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Client
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              data-testid="client-search-input"
              type="text"
              placeholder="Search clients by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 rounded-full"
            />
          </div>
        </div>

        {/* Clients Grid */}
        {filteredClients.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 text-center border-2 border-dashed border-slate-200">
            <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-heading font-semibold mb-2">No clients found</h3>
            <p className="text-slate-600 mb-6">
              {searchQuery ? 'Try adjusting your search' : 'Start by adding your first client'}
            </p>
            {!searchQuery && (
              <Button
                onClick={() => setShowAddModal(true)}
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-full font-heading"
              >
                Add First Client
              </Button>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredClients.map((client) => (
              <div
                key={client.id}
                onClick={() => navigate(`/broker/clients/${client.id}`)}
                className="bg-white rounded-3xl p-6 border-2 border-slate-100 hover:border-violet-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-violet-100 flex items-center justify-center text-violet-600 font-heading font-bold text-xl">
                    {client.client_name.charAt(0)}
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    client.status === 'active' ? 'bg-green-100 text-green-800' :
                    client.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {client.status}
                  </span>
                </div>
                <h3 className="text-lg font-heading font-semibold mb-1">{client.client_name}</h3>
                <p className="text-sm text-slate-600 mb-4">{client.client_email}</p>
                <div className="space-y-2">
                  {client.current_score_experian && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Experian:</span>
                      <span className="font-semibold">{client.current_score_experian}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Disputes:</span>
                    <span className="font-semibold">{client.total_disputes}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Client Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-bold">Add New Client</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddClient} className="space-y-4 mt-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Client Name *</Label>
                <Input
                  data-testid="new-client-name"
                  required
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  placeholder="John Doe"
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  data-testid="new-client-email"
                  type="email"
                  required
                  value={formData.client_email}
                  onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                  placeholder="john@example.com"
                  className="rounded-xl"
                />
              </div>
            </div>
            
            <div>
              <Label>Phone</Label>
              <Input
                value={formData.client_phone}
                onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                placeholder="(555) 123-4567"
                className="rounded-xl"
              />
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>Experian Score</Label>
                <Input
                  type="number"
                  value={formData.current_score_experian}
                  onChange={(e) => setFormData({ ...formData, current_score_experian: e.target.value })}
                  placeholder="650"
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>Equifax Score</Label>
                <Input
                  type="number"
                  value={formData.current_score_equifax}
                  onChange={(e) => setFormData({ ...formData, current_score_equifax: e.target.value })}
                  placeholder="645"
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>TransUnion Score</Label>
                <Input
                  type="number"
                  value={formData.current_score_transunion}
                  onChange={(e) => setFormData({ ...formData, current_score_transunion: e.target.value })}
                  placeholder="655"
                  className="rounded-xl"
                />
              </div>
            </div>

            <div>
              <Label>Goal Score</Label>
              <Input
                type="number"
                value={formData.goal_score}
                onChange={(e) => setFormData({ ...formData, goal_score: e.target.value })}
                placeholder="720"
                className="rounded-xl"
              />
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional information about this client..."
                className="rounded-xl min-h-[100px]"
              />
            </div>

            <Button
              data-testid="submit-new-client"
              type="submit"
              className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full py-6 font-heading font-semibold"
            >
              Add Client
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Modal */}
      <Dialog open={showBulkImportModal} onOpenChange={setShowBulkImportModal}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-bold">Bulk Import Clients</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            <div className="bg-blue-50 rounded-2xl p-6 border border-blue-100">
              <h3 className="font-heading font-semibold text-blue-900 mb-2">📋 CSV Format Required</h3>
              <p className="text-sm text-blue-800 mb-3">
                Your CSV file must include the following columns (in this order):
              </p>
              <div className="bg-white rounded-xl p-3 text-xs font-mono text-slate-700">
                client_name,client_email,client_phone,current_score_experian,current_score_equifax,current_score_transunion,goal_score,notes
              </div>
              <Button
                onClick={downloadSampleCSV}
                variant="outline"
                className="mt-3 rounded-full text-sm"
              >
                Download Sample CSV
              </Button>
            </div>

            <div>
              <Label>Select CSV File *</Label>
              <Input
                type="file"
                accept=".csv"
                onChange={(e) => setUploadFile(e.target.files[0])}
                className="rounded-xl"
              />
              {uploadFile && (
                <p className="text-sm text-slate-600 mt-2">
                  Selected: {uploadFile.name}
                </p>
              )}
            </div>

            <div className="bg-yellow-50 rounded-2xl p-4 border border-yellow-100">
              <p className="text-sm text-yellow-800">
                <strong>⚠️ Note:</strong> Duplicate emails will be skipped. Only required fields are client_name and client_email. 
                All other fields are optional.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowBulkImportModal(false);
                  setUploadFile(null);
                }}
                variant="outline"
                className="flex-1 rounded-full font-heading"
              >
                Cancel
              </Button>
              <Button
                onClick={handleBulkImport}
                disabled={!uploadFile || importing}
                className="flex-1 bg-violet-600 hover:bg-violet-700 text-white rounded-full font-heading"
              >
                {importing ? (
                  <>
                    <Sparkles className="w-4 h-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import Clients
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BrokerClients;
